/**
 * Sortable results grid for Sales Comps.
 * Displays matching properties with column-header sorting, CSV export, and summary stats.
 */

import { useState, useMemo, useCallback } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown, Download } from "lucide-react";
import type { CompSale, CompSearchResponse } from "../../api/comps";
import { fmtCurrency, fmtPSF, fmtSF, fmtDate } from "../../utils/format";

const PROPERTY_TYPE_BADGE: Record<string, string> = {
  Retail: "bg-green-100 text-green-800",
  Office: "bg-blue-100 text-blue-800",
  Industrial: "bg-amber-100 text-amber-800",
  Hospitality: "bg-purple-100 text-purple-800",
  "Special Purpose": "bg-gray-100 text-gray-700",
  Commercial: "bg-teal-100 text-teal-800",
};

type SortKey =
  | "sale_date"
  | "address"
  | "city"
  | "property_type"
  | "building_sf"
  | "land_sf"
  | "year_built"
  | "floor_count"
  | "sale_price"
  | "price_psf";
type SortDir = "asc" | "desc";

interface CompsGridProps {
  response: CompSearchResponse | null;
  loading: boolean;
  selectedParcelId: string | null;
  onRowClick: (parcelId: string) => void;
}

export default function CompsGrid({
  response,
  loading,
  selectedParcelId,
  onRowClick,
}: CompsGridProps) {
  const [sortKey, setSortKey] = useState<SortKey>("sale_date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const toggleSort = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        setSortDir("desc");
      }
    },
    [sortKey]
  );

  const sorted = useMemo(() => {
    if (!response?.results) return [];
    const rows = [...response.results];
    rows.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "string" && typeof bv === "string") {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      const diff = (av as number) - (bv as number);
      return sortDir === "asc" ? diff : -diff;
    });
    return rows;
  }, [response?.results, sortKey, sortDir]);

  const exportCSV = useCallback(() => {
    if (!sorted.length) return;
    const headers = [
      "Sale Date",
      "Address",
      "City",
      "Property Type",
      "DOR Code",
      "Building SF",
      "Land SF",
      "Year Built",
      "Stories",
      "Units",
      "Sale Price",
      "$/SF",
      "County",
      "Parcel ID",
    ];
    const rows = sorted.map((r) => [
      r.sale_date || "",
      `"${r.address}"`,
      `"${r.city}"`,
      r.property_type,
      r.dor_code,
      r.building_sf ?? "",
      r.land_sf ?? "",
      r.year_built ?? "",
      r.floor_count ?? "",
      r.unit_count ?? "",
      r.sale_price ?? "",
      r.price_psf ?? "",
      r.county,
      r.parcel_id,
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "comps_export.csv";
    a.click();
    URL.revokeObjectURL(url);
  }, [sorted]);

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col)
      return <ArrowUpDown className="w-3 h-3 text-bark-300 ml-1 inline" />;
    return sortDir === "asc" ? (
      <ArrowUp className="w-3 h-3 text-forest-700 ml-1 inline" />
    ) : (
      <ArrowDown className="w-3 h-3 text-forest-700 ml-1 inline" />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-forest-700" />
      </div>
    );
  }

  if (!response) {
    return (
      <div className="flex items-center justify-center h-64 text-bark-400 text-sm">
        Draw a search area on the map and select property types to begin.
      </div>
    );
  }

  if (response.results.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-bark-400 text-sm">
        No results found. Try expanding your search area or adjusting filters.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Summary stats */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-4 text-sm">
          <span className="text-bark-600">
            <strong className="text-bark-800">{response.total_count}</strong> results
          </span>
          {response.avg_price_psf != null && (
            <span className="text-bark-600">
              Avg: <strong className="text-bark-800">{fmtPSF(response.avg_price_psf)}</strong>
            </span>
          )}
          {response.median_sale_price != null && (
            <span className="text-bark-600">
              Median: <strong className="text-bark-800">{fmtCurrency(response.median_sale_price)}</strong>
            </span>
          )}
        </div>
        <button
          onClick={exportCSV}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-bark-600 border border-grove-300 rounded-lg hover:bg-grove-100 transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </button>
      </div>

      {response.truncated && (
        <div className="bg-honey-100 text-honey-900 text-xs px-3 py-2 rounded-lg border border-honey-300">
          Too many results. Draw a smaller search area for better performance.
        </div>
      )}

      {/* Data table */}
      <div className="overflow-x-auto rounded-lg border border-grove-300">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-grove-100 text-bark-600">
              {(
                [
                  ["sale_date", "Sale Date"],
                  ["address", "Address"],
                  ["city", "City"],
                  ["property_type", "Type"],
                  ["building_sf", "Bldg SF"],
                  ["land_sf", "Land SF"],
                  ["year_built", "Yr Built"],
                  ["floor_count", "Stories"],
                  ["sale_price", "Sale Price"],
                  ["price_psf", "$/SF"],
                ] as [SortKey, string][]
              ).map(([key, label]) => (
                <th
                  key={key}
                  onClick={() => toggleSort(key)}
                  className="px-3 py-2 text-left font-medium text-xs cursor-pointer select-none whitespace-nowrap hover:bg-grove-200 transition-colors"
                >
                  {label}
                  <SortIcon col={key} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-grove-200">
            {sorted.map((comp, idx) => (
              <tr
                key={`${comp.parcel_id}-${comp.sale_date ?? idx}`}
                onClick={() => onRowClick(comp.parcel_id)}
                className={`cursor-pointer transition-colors ${
                  selectedParcelId === comp.parcel_id
                    ? "bg-forest-50 border-l-2 border-forest-500"
                    : "hover:bg-grove-50"
                }`}
              >
                <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                  {fmtDate(comp.sale_date)}
                </td>
                <td className="px-3 py-2 max-w-[180px] truncate" title={comp.address}>
                  {comp.address || "N/A"}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">
                  {comp.city || "—"}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                      PROPERTY_TYPE_BADGE[comp.property_type] || "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {comp.property_type}
                  </span>
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                  {fmtSF(comp.building_sf)}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                  {fmtSF(comp.land_sf)}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                  {comp.year_built ?? "—"}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                  {comp.floor_count ?? "—"}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                  {fmtCurrency(comp.sale_price)}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                  {fmtPSF(comp.price_psf)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
